import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { useRichTextEditorContext } from "@mantine/tiptap";

export const TableMenu: React.FC<{}> = () => {
    const { editor } = useRichTextEditorContext();

    if (!editor) {
        return null;
    }

    const toggleBordersByPos = (
        tablePos: number,
        currentBordersState?: boolean
    ) => {
        if (tablePos === -1) return;
        const { state } = editor;
        const tableNode = state.doc.nodeAt(tablePos);
        if (!tableNode) return;
        const currentBorders =
            currentBordersState ?? Boolean(tableNode.attrs.border);
        const newBorderValue = currentBorders ? null : "1px solid black";

        const transaction = state.tr;

        // Set border on table
        transaction.setNodeMarkup(tablePos, undefined, {
            ...tableNode.attrs,
            border: newBorderValue,
        });

        // Find and update all table cells
        tableNode.descendants((node, pos) => {
            if (
                node.type.name === "tableCell" ||
                node.type.name === "tableHeader"
            ) {
                const absolutePos = tablePos + 1 + pos;
                transaction.setNodeMarkup(absolutePos, undefined, {
                    ...node.attrs,
                    border: newBorderValue,
                });
            }
        });

        editor.view.dispatch(transaction);
    };

    const toggleSelectedTableBorders = () => {
        if (!editor?.isActive("table")) return;
        const { state } = editor;
        const { from } = state.selection;
        const $pos = state.doc.resolve(from);

        // Find the table pos
        let tablePos = -1;
        for (let depth = $pos.depth; depth > 0; depth--) {
            const node = $pos.node(depth);
            if (node.type.name === "table") {
                tablePos = $pos.before(depth);
                break;
            }
        }

        toggleBordersByPos(tablePos);
    };

    const toggleAllTableBorders = () => {
        // get all table positions
        const { state } = editor;
        const tablePositions: number[] = [];
        state.doc.descendants((node, pos) => {
            if (node.type.name === "table" && node.attrs.editable) {
                tablePositions.push(pos);
            }
        });
        const firstTableNode = state.doc.nodeAt(tablePositions[0]);
        const currentBordersState = Boolean(firstTableNode?.attrs.border);
        tablePositions.forEach((pos) =>
            toggleBordersByPos(pos, currentBordersState)
        );
    };

    const optionButtonClassName =
        "text-start leading-tight w-full rounded-lg py-1.5 px-3 data-[focus]:bg-black/5 disabled:opacity-40";

    return (
        <Menu>
            <MenuButton
                aria-label="Table menu"
                title="Table menu"
                className="flex items-center gap-2"
            >
                <TableCellsIcon className="size-4" />
                <ChevronDownIcon className="size-3" />
            </MenuButton>

            <MenuItems
                transition
                anchor="bottom end"
                className="w-52 origin-top-right rounded-xl border border-gray-200 bg-white p-1 text-sm/6 transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            >
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor
                                ?.chain()
                                .focus()
                                .insertTable({
                                    rows: 3,
                                    cols: 3,
                                    withHeaderRow: true,
                                })
                                .run()
                        }
                    >
                        Insert Table
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={toggleAllTableBorders}
                    >
                        Toggle all borders
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        disabled={!editor?.isActive("table")}
                        onClick={toggleSelectedTableBorders}
                    >
                        Toggle table border
                    </button>
                </MenuItem>
                <div className="my-1 h-px bg-black/10" />
                <MenuItem>
                    <button
                        className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]hover:bg-gray-200"
                        onClick={() =>
                            editor?.chain().focus().addColumnBefore().run()
                        }
                    >
                        Add column before
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().addColumnAfter().run()
                        }
                    >
                        Add column after
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().deleteColumn().run()
                        }
                    >
                        Delete column
                    </button>
                </MenuItem>
                <div className="my-1 h-px bg-black/10" />
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().addRowBefore().run()
                        }
                    >
                        Add row before
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().addRowAfter().run()
                        }
                    >
                        Add row after
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().deleteRow().run()
                        }
                    >
                        Delete row
                    </button>
                </MenuItem>
                <div className="my-1 h-px bg-black/10" />
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().toggleHeaderColumn().run()
                        }
                    >
                        Toggle header column
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().toggleHeaderRow().run()
                        }
                    >
                        Toggle header row
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().toggleHeaderCell().run()
                        }
                    >
                        Toggle header cell
                    </button>
                </MenuItem>
                <div className="my-1 h-px bg-black/10" />
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().deleteTable().run()
                        }
                    >
                        Delete table
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().mergeCells().run()
                        }
                    >
                        Merge cells
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().splitCell().run()
                        }
                    >
                        Split cell
                    </button>
                </MenuItem>
                <div className="my-1 h-px bg-black/10" />

                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().mergeOrSplit().run()
                        }
                    >
                        Merge or split
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        className={optionButtonClassName}
                        onClick={() =>
                            editor?.chain().focus().fixTables().run()
                        }
                    >
                        Fix tables
                    </button>
                </MenuItem>
            </MenuItems>
        </Menu>
    );
};
